import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { PaywallProvider } from "@/contexts/PaywallContext";
import { useAuth } from "@/contexts/AuthContext";
import React, { Suspense, lazy, useEffect } from 'react';
import deepLinkService from "@/services/deep-link-service";
import { initBackButtonHandler } from "@/services/navigation-service";
import { SafeAreaProvider } from "@/components/SafeAreaProvider";

import { Capacitor } from '@capacitor/core'
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor'

// import DebugBubble from "@/components/DebugBubble";
// Import Mixpanel SDK
import mixpanel from "mixpanel-browser";

// Import Analytics service
import { analytics } from "@/services/analytics";


// Force build - 2026-02-06
// Loader component for Suspense fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Lazy loaded pages
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const EmailIntake = lazy(() => import("./pages/EmailIntake"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const EnterPassword = lazy(() => import("./pages/EnterPassword"));
const MagicLinkSent = lazy(() => import("./pages/MagicLinkSent"));
const Home = lazy(() => import("./pages/Home"));
const Story = lazy(() => import("./pages/Story"));
const IP = lazy(() => import("./pages/IP"));
const Chapter = lazy(() => import("./pages/Chapter"));
const ChapterPreview = lazy(() => import("./pages/ChapterPreview"));
const StoryPreview = lazy(() => import("./pages/StoryPreview"));
const Settings = lazy(() => import("./pages/Settings"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const Shop = lazy(() => import("./pages/Shop"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Index = lazy(() => import("./pages/Index"));
const Library = lazy(() => import("./pages/Library"));
const Discover = lazy(() => import("./pages/Discover"));
const AllStories = lazy(() => import("./pages/AllStories"));
const AllIPs = lazy(() => import("./pages/AllIPs"));
const DebugInfo = lazy(() => import("./pages/DebugInfo"));
const ReportedComments = lazy(() => import("./pages/ReportedComments"));
const ChapterNotFound = lazy(() => import("./components/ChapterNotFound"));
const ExternalRedirect = lazy(() => import("./components/ExternalRedirect"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AdminStoryPreviews = lazy(() => import("./pages/AdminStoryPreviews"));
const FanficReader = lazy(() => import("./pages/FanficReader"));
const Characters = lazy(() => import("./pages/Characters"));
const CharacterChat = lazy(() => import("./pages/CharacterChat"));
const Conversations = lazy(() => import("./pages/Conversations"));
const FanficCreator = lazy(() => import("./pages/FanficCreator"));
const FanficGenerating = lazy(() => import("./pages/FanficGenerating"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const UserPublicProfile = lazy(() => import("./pages/UserPublicProfile"));
const UserFollowList = lazy(() => import("./pages/UserFollowList"));
const Users = lazy(() => import("./pages/Users"));
const AudioPage = lazy(() => import("./pages/AudioPage"));
const VideoPage = lazy(() => import("./pages/VideoPage"));
const ViewCharacters = lazy(() => import("./pages/ViewCharacters"));
const GetApp = lazy(() => import("./pages/GetApp"));

// Create a stable queryClient instance with improved settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
  },
});

// ScrollToTop component to handle scroll position on navigation
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// DeepLinkHandler component to initialize deep link handling and back button
const DeepLinkHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useEffect(() => {
    deepLinkService.init();
    initBackButtonHandler();
    console.log('Deep link and back button handlers initialized');
  }, []);

  return <>{children}</>;
};

// ProtectedRoute component to handle authentication
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!user) {
    return <Navigate to="/email-intake" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Public routes that don't require authentication
const publicRoutes = ['/login', '/signup', '/reset-password', '/email-intake', '/set-password', '/enter-password', '/magic-link-sent', '/preview/chapter/:id', '/preview/story/:id', '/privacy-policy', '/terms-of-service', '/get-app'];

const App = () => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  useEffect(() => {

    // RevenueCat configuration
    const apiKey = Capacitor.getPlatform() === 'ios'
      ? 'appl_exbRsNECGMZSSGqoJcncasSCGXn'
      : 'goog_IaxPgidZWWJESOQMUwfrGWgaswb';

    (async () => {
      try {
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
        await Purchases.configure({ apiKey });
        console.log('✅ RevenueCat configurado com sucesso');
      } catch (err) {
        console.error('❌ Erro ao configurar RevenueCat:', err);
      }
    })();

    (async () => {
      try {
        mixpanel.init("ba0e072f021966bd1933238ad34fb9a8", {
          debug: true,
          track_pageview: false,
          persistence: "localStorage",
        });
        console.log('✅ Mixpanel configured');

        await analytics.init();
        await analytics.trackInstall();

        console.log('✅ Analytics configured with ATT compliance');
      } catch (err) {
        console.error('❌ Error configuring analytics:', err);
      }
    })();


  }, [user]);

  // Handle user identification when auth state changes
  useEffect(() => {
    if (isLoading) return;

    if (user) {
      console.log('🔍 User authenticated, identifying in Mixpanel...');
      analytics.identifyUser(user);
    } else {
      console.log('🚪 User logged out, resetting Mixpanel...');
      analytics.reset();
    }
  }, [user, isLoading]);

  const isPublicRoute = publicRoutes.some(route => {
    if (route.includes(':')) {
      const routePattern = route.replace(/:\w+/g, '[^/]+');
      const regex = new RegExp(`^${routePattern}$`);
      return regex.test(location.pathname);
    }
    return route === location.pathname;
  });

  const isAuthRoute = ['/login', '/signup', '/reset-password', '/email-intake', '/set-password', '/enter-password', '/magic-link-sent'].includes(location.pathname);

  if (user && isAuthRoute && !isLoading) {
    return <Navigate to="/home" replace />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ImpersonationProvider>
        <PaywallProvider>
          <SafeAreaProvider>
            <DeepLinkHandler>
              <TooltipProvider>
              <ScrollToTop />
              <Toaster />
              <Sonner />
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/login" element={<Navigate to="/email-intake" replace />} />
                  <Route path="/signup" element={<Navigate to="/email-intake" replace />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/email-intake" element={<EmailIntake />} />
                  <Route path="/set-password" element={<SetPassword />} />
                  <Route path="/enter-password" element={<EnterPassword />} />
                  <Route path="/magic-link-sent" element={<MagicLinkSent />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />

                  <Route path="/preview/chapter/:id" element={<ChapterPreview />} />
                  <Route path="/preview/story/:id" element={<StoryPreview />} />

                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/get-app" element={<GetApp />} />

                  <Route path="/debug" element={<DebugInfo />} />

                  <Route path="/admin/story-previews" element={<ProtectedRoute><AdminStoryPreviews /></ProtectedRoute>} />

                  <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
                  <Route path="/index" element={<ProtectedRoute><Index /></ProtectedRoute>} />
                  <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                  <Route path="/story/:id" element={<ProtectedRoute><Story /></ProtectedRoute>} />
                  <Route path="/ip/:id" element={<ProtectedRoute><IP /></ProtectedRoute>} />
                  <Route path="/ip/:storyId/audio/:audioId" element={<ProtectedRoute><AudioPage /></ProtectedRoute>} />
                  <Route path="/ip/:storyId/video/:videoId" element={<ProtectedRoute><VideoPage /></ProtectedRoute>} />
                  <Route path="/chapter/:id" element={<ProtectedRoute><Chapter /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                  <Route path="/settings/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
                  <Route path="/settings/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
                  <Route path="/profile/:username" element={<ProtectedRoute><UserPublicProfile /></ProtectedRoute>} />
                  <Route path="/profile/:username/:type" element={<ProtectedRoute><UserFollowList /></ProtectedRoute>} />
                  <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
                  <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
                  <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
                  <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
                  <Route path="/stories" element={<ProtectedRoute><AllStories /></ProtectedRoute>} />
                  <Route path="/all-ips" element={<ProtectedRoute><AllIPs /></ProtectedRoute>} />
                  <Route path="/admin/reports" element={<ProtectedRoute><ReportedComments /></ProtectedRoute>} />
                  <Route path="/fanfic/:id" element={<ProtectedRoute><FanficReader /></ProtectedRoute>} />
                  <Route path="/characters" element={<ProtectedRoute><Characters /></ProtectedRoute>} />
                  <Route path="/view-characters" element={<ProtectedRoute><ViewCharacters /></ProtectedRoute>} />
                  <Route path="/chat/:characterId" element={<ProtectedRoute><CharacterChat /></ProtectedRoute>} />
                  <Route path="/conversations" element={<ProtectedRoute><Conversations /></ProtectedRoute>} />
                  <Route path="/create/fanfic" element={<ProtectedRoute><FanficCreator /></ProtectedRoute>} />
                  <Route path="/create/fanfic/generating/:fanficId" element={<ProtectedRoute><FanficGenerating /></ProtectedRoute>} />

                  <Route path="/settings/about" element={<ExternalRedirect url="https://pulsefiction.app" />} />
                  <Route path="/settings/data-request" element={<ExternalRedirect url="https://pulsefiction.app/data-deletion" />} />

                  <Route path="/chapter-not-found" element={<ChapterNotFound />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              {/* <DebugBubble /> */}
            </TooltipProvider>
          </DeepLinkHandler>
        </SafeAreaProvider>
      </PaywallProvider>
      </ImpersonationProvider>
    </QueryClientProvider>
  );
};

export default App;